import React, { useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { offset, flip, shift } from "@floating-ui/react-dom";
import {
  CreateEditInstallment,
  CreateInstallment,
  PreviewInstallment,
  installmentListApi,
} from "../../service/CustomerSupport";
import AppContext from "../../context/AppContext";
import { toast } from "../../utils/toast";
import PDFViewerComponent from "./PDFViewerComponent";
import PaymentTable from "./PaymentTable";
import CommonButton from "../common/CommonButton";
import translations from "../common/Translations";
import { ChevronDown, Loader2 } from "lucide-react";
import MultiAttachInput from "../common/MultiAttachInput";
import { validateAttachments } from "../../utils/attachments";
import { pdf } from "@react-pdf/renderer";
import { MyDocument } from "./PDFViewerComponent"; // we will export MyDocument (see note below)

// The real limits come from GET /instalment-plan/get, which returns
// maxInstalments / minOutstandingAmount / canCreateNewPlan on the top-level
// body.value (null = no limit, i.e. super admin). A brand-new account answers
// 601 "No instalment plans found", so there are no limits to read -- these
// role defaults mirror the server rules to show the right cap up front.
// Everything is re-enforced server-side; this is presentation only.
const ROLE_PLAN_LIMITS = {
  super_admin: { maxInstalments: null },
  admin: { maxInstalments: 30 },
  office_user: { maxInstalments: 24 },
  supervisor: { maxInstalments: 24 },
  field_agent: { maxInstalments: 24 },
};
// Unknown/missing role -> the most restrictive cap rather than an open field.
const FALLBACK_PLAN_LIMITS = { maxInstalments: 24 };

const getCustProjectId = () =>
  (localStorage.getItem("CustprojectId") || "").replace(/"/g, "");

const parseDateTime = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDateTimeForApi = (date) => {
  if (!date) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:00`;
};

const installmentDatePickerModifiers = [
  offset(8),
  flip({
    padding: 16,
    fallbackPlacements: ["bottom-start", "bottom-end", "top-start", "top-end"],
  }),
  shift({ padding: 16 }),
];

/** Portal calendar to body so layout/positioning is not broken by parent containers. */
function installmentDatePickerPopper({ children }) {
  if (typeof document === "undefined") {
    return children;
  }
  return createPortal(
    <div
      className="installment-datepicker-portal"
      style={{
        position: "relative",
        zIndex: 100000,
        isolation: "isolate",
      }}
    >
      {children}
    </div>,
    document.body
  );
}

const installmentDatePickerClassName = (theme) =>
  `w-full h-[50px] px-4 rounded-lg border border-gray-300 text-sm text-gray-700 box-border focus:border-blue-500 focus:outline-none focus:ring-0 ${
    theme === "dark"
      ? "dark-mode border-gray-200 placeholder-white"
      : theme === "high-contrast"
        ? "high-contrast"
        : ""
  }`;

// Same canonical field height/style as Interactive_Rc.jsx/Special_Rc.jsx so
// every Customer Support tab lines up at the same 50px.
const FIELD_INPUT_CLASS =
  "h-[50px] px-4 rounded-lg border border-gray-300 bg-white text-gray-700 w-full text-sm focus:border-blue-500 focus:outline-none focus:ring-0";

const CreateInstallmentPlan = ({ customVarPerm, onHeaderActionsChange }) => {
  const tableRef = useRef();
  const [isPreviewVisible, setIsPreviewVisible] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  console.log('previewData: ', previewData);
  const [accountNo, setAccountNo] = useState("");
  const [customerID, setCustomerId] = useState();
  const [phone, setPhone] = useState("");
  const [numInstallments, setNumInstallments] = useState();
  const [advancePayment, setAdvancePayment] = useState();
  const [advanceDate, setAdvanceDate] = useState("");
  const [firstInstallmentDate, setFirstInstallmentDate] = useState("");
  const { userType, theme, pdfData, langMode, projectId } =
    useContext(AppContext);
  const [countryDetail, setCountryDetail] = useState([
    { name: "Afghanistan", length: 9, countryCode: "+93" },
    { name: "Albania", length: 8, countryCode: "+355" },
    { name: "Algeria", length: 9, countryCode: "+213" },
    { name: "Andorra", length: 6, countryCode: "+376" },
    { name: "Angola", length: 9, countryCode: "+244" },
    { name: "Argentina", length: 10, countryCode: "+54" },
    { name: "Armenia", length: 8, countryCode: "+374" },
    { name: "Aruba", length: 7, countryCode: "+297" },
    { name: "Australia", length: 9, countryCode: "+61" },
    { name: "Austria", length: 12, countryCode: "+43" },
    { name: "Azerbaijan", length: 9, countryCode: "+994" },
    { name: "Antigua and Barbuda", length: 7, countryCode: "+1268" },
    { name: "Bahamas", length: 7, countryCode: "+1 242" },
    { name: "Bahrain", length: 8, countryCode: "+973" },
    { name: "Bangladesh", length: 10, countryCode: "+880" },
    { name: "Barbados", length: 10, countryCode: "+1 246" },
    { name: "Belarus", length: 9, countryCode: "+375" },
    { name: "Belgium", length: 9, countryCode: "+32" },
    { name: "Belize", length: 7, countryCode: "+501" },
    { name: "Benin", length: 8, countryCode: "+229" },
    { name: "Bhutan", length: 8, countryCode: "+975" },
    { name: "Bolivia", length: 8, countryCode: "+591" },
    { name: "Bosnia and Herzegovina", length: 8, countryCode: "+387" },
    { name: "Botswana", length: 7, countryCode: "+267" },
    { name: "Brazil", length: 10, countryCode: "+55" },
    { name: "Brunei", length: 7, countryCode: "+673" },
    { name: "Bulgaria", length: 8, countryCode: "+359" },
    { name: "Burkina Faso", length: 10, countryCode: "+226" },
    { name: "Burundi", length: 8, countryCode: "+257" },
    { name: "British Indian Ocean Territiry", length: 7, countryCode: "+246" },
    { name: "Cabo Verde", length: 7, countryCode: "+238" },
    { name: "Cambodia", length: 9, countryCode: "+855" },
    { name: "Cameroon", length: 9, countryCode: "+237" },
    { name: "Canada", length: 11, countryCode: "+1" },
    { name: "Central African Republic", length: 10, countryCode: "+236" },
    { name: "Chad", length: 8, countryCode: "+235" },
    { name: "Chile", length: 9, countryCode: "+56" },
    { name: "China", length: 11, countryCode: "+86" },
    { name: "Colombia", length: 10, countryCode: "+57" },
    { name: "Comoros", length: 7, countryCode: "+269" },
    { name: "Congo (Congo-Brazzaville)", length: 10, countryCode: "+242" },
    { name: "Costa Rica", length: 8, countryCode: "+506" },
    { name: "Croatia", length: 9, countryCode: "+385" },
    { name: "Cuba", length: 8, countryCode: "+53" },
    { name: "Cyprus", length: 8, countryCode: "+357" },
    { name: "Czech Republic", length: 9, countryCode: "+420" },
    { name: "Denmark", length: 8, countryCode: "+45" },
    { name: "Djibouti", length: 7, countryCode: "+253" },
    { name: "Dominica", length: 10, countryCode: "+1 767" },
    { name: "Dominican Republic", length: 10, countryCode: "+1 809" },
    { name: "Ecuador", length: 9, countryCode: "+593" },
    { name: "Egypt", length: 10, countryCode: "+20" },
    { name: "El Salvador", length: 8, countryCode: "+503" },
    { name: "Equatorial Guinea", length: 9, countryCode: "+240" },
    { name: "Eritrea", length: 8, countryCode: "+291" },
    { name: "Estonia", length: 7, countryCode: "+372" },
    { name: "Eswatini", length: 8, countryCode: "+268" },
    { name: "Ethiopia", length: 9, countryCode: "+251" },
    { name: "Fiji", length: 7, countryCode: "+679" },
    { name: "Finland", length: 12, countryCode: "+358" },
    { name: "France", length: 9, countryCode: "+33" },
    { name: "Gabon", length: 8, countryCode: "+241" },
    { name: "Gambia", length: 7, countryCode: "+220" },
    { name: "Georgia", length: 9, countryCode: "+995" },
    { name: "Germany", length: 11, countryCode: "+49" },
    { name: "Ghana", length: 9, countryCode: "+233" },
    { name: "Greece", length: 10, countryCode: "+30" },
    { name: "Grenada", length: 10, countryCode: "+1 473" },
    { name: "Guatemala", length: 8, countryCode: "+502" },
    { name: "Guinea", length: 9, countryCode: "+224" },
    { name: "Guinea-Bissau", length: 7, countryCode: "+245" },
    { name: "Guyana", length: 10, countryCode: "+592" },
    { name: "Haiti", length: 8, countryCode: "+509" },
    { name: "Honduras", length: 8, countryCode: "+504" },
    { name: "Hungary", length: 9, countryCode: "+36" },
    { name: "Iceland", length: 7, countryCode: "+354" },
    { name: "India", length: 10, countryCode: "+91" },
    { name: "Indonesia", length: 10, countryCode: "+62" },
    { name: "Iran", length: 10, countryCode: "+98" },
    { name: "Iraq", length: 10, countryCode: "+964" },
    { name: "Ireland", length: 7, countryCode: "+353" },
    { name: "Israel", length: 9, countryCode: "+972" },
    { name: "Italy", length: 10, countryCode: "+39" },
    { name: "Jamaica", length: 10, countryCode: "+1 876" },
    { name: "Japan", length: 10, countryCode: "+81" },
    { name: "Jordan", length: 9, countryCode: "+962" },
    { name: "Kazakhstan", length: 10, countryCode: "+7" },
    { name: "Kenya", length: 10, countryCode: "+254" },
    { name: "Kiribati", length: 7, countryCode: "+686" },
    { name: "Kuwait", length: 8, countryCode: "+965" },
    { name: "Kyrgyzstan", length: 9, countryCode: "+996" },
    { name: "Laos", length: 8, countryCode: "+856" },
    { name: "Latvia", length: 8, countryCode: "+371" },
    { name: "Lebanon", length: 8, countryCode: "+961" },
    { name: "Lesotho", length: 8, countryCode: "+266" },
    { name: "Liberia", length: 7, countryCode: "+231" },
    { name: "Libya", length: 9, countryCode: "+218" },
    { name: "Liechtenstein", length: 10, countryCode: "+423" },
    { name: "Lithuania", length: 9, countryCode: "+370" },
    { name: "Luxembourg", length: 9, countryCode: "+352" },
    { name: "Madagascar", length: 9, countryCode: "+261" },
    { name: "Malawi", length: 10, countryCode: "+265" },
    { name: "Malaysia", length: 10, countryCode: "+60" },
    { name: "Maldives", length: 7, countryCode: "+960" },
    { name: "Mali", length: 8, countryCode: "+223" },
    { name: "Malta", length: 8, countryCode: "+356" },
    { name: "Marshall Islands", length: 7, countryCode: "+692" },
    { name: "Mauritania", length: 8, countryCode: "+222" },
    { name: "Mauritius", length: 10, countryCode: "+230" },
    { name: "Mexico", length: 10, countryCode: "+52" },
    { name: "Micronesia", length: 7, countryCode: "+691" },
    { name: "Moldova", length: 8, countryCode: "+373" },
    { name: "Monaco", length: 8, countryCode: "+377" },
    { name: "Mongolia", length: 8, countryCode: "+976" },
    { name: "Montenegro", length: 8, countryCode: "+382" },
    { name: "Morocco", length: 10, countryCode: "+212" },
    { name: "Mozambique", length: 9, countryCode: "+258" },
    { name: "Myanmar (formerly Burma)", length: 9, countryCode: "+95" },
    { name: "Namibia", length: 9, countryCode: "+264" },
    { name: "Nauru", length: 7, countryCode: "+674" },
    { name: "Nepal", length: 10, countryCode: "+977" },
    { name: "Netherlands", length: 10, countryCode: "+31" },
    { name: "New Zealand", length: 9, countryCode: "+64" },
    { name: "Nicaragua", length: 8, countryCode: "+505" },
    { name: "Niger", length: 8, countryCode: "+227" },
    { name: "Nigeria", length: 10, countryCode: "+234" },
    { name: "North Korea", length: 9, countryCode: "+850" },
    { name: "North Macedonia", length: 8, countryCode: "+389" },
    { name: "Norway", length: 8, countryCode: "+47" },
    { name: "Oman", length: 8, countryCode: "+968" },
    { name: "Pakistan", length: 10, countryCode: "+92" },
    { name: "Palau", length: 7, countryCode: "+680" },
    { name: "Palestine", length: 10, countryCode: "+970" },
    { name: "Panama", length: 7, countryCode: "+507" },
    { name: "Papua New Guinea", length: 10, countryCode: "+675" },
    { name: "Paraguay", length: 9, countryCode: "+595" },
    { name: "Peru", length: 9, countryCode: "+51" },
    { name: "Philippines", length: 10, countryCode: "+63" },
    { name: "Poland", length: 9, countryCode: "+48" },
    { name: "Portugal", length: 9, countryCode: "+351" },
    { name: "Qatar", length: 8, countryCode: "+974" },
    { name: "Romania", length: 10, countryCode: "+40" },
    { name: "Russia", length: 10, countryCode: "+7" },
    { name: "Rwanda", length: 10, countryCode: "+250" },
    { name: "Saint Kitts and Nevis", length: 10, countryCode: "+1 869" },
    { name: "Saint Lucia", length: 10, countryCode: "+1 758" },
    {
      name: "Saint Vincent and the Grenadines",
      length: 10,
      countryCode: "+1 784",
    },
    { name: "Samoa", length: 7, countryCode: "+685" },
    { name: "San Marino", length: 10, countryCode: "+378" },
    { name: "Sao Tome and Principe", length: 7, countryCode: "+239" },
    { name: "Saudi Arabia", length: 9, countryCode: "+966" },
    { name: "Senegal", length: 9, countryCode: "+221" },
    { name: "Serbia", length: 8, countryCode: "+381" },
    { name: "Seychelles", length: 7, countryCode: "+248" },
    { name: "Sierra Leone", length: 8, countryCode: "+232" },
    { name: "Singapore", length: 8, countryCode: "+65" },
    { name: "Slovakia", length: 9, countryCode: "+421" },
    { name: "Slovenia", length: 8, countryCode: "+386" },
    { name: "Solomon Islands", length: 7, countryCode: "+677" },
    { name: "Somalia", length: 8, countryCode: "+252" },
    { name: "South Africa", length: 10, countryCode: "+27" },
    { name: "South Korea", length: 10, countryCode: "+82" },
    { name: "South Sudan", length: 9, countryCode: "+211" },
    { name: "Spain", length: 9, countryCode: "+34" },
    { name: "Sri Lanka", length: 10, countryCode: "+94" },
    { name: "Sudan", length: 9, countryCode: "+249" },
    { name: "Suriname", length: 7, countryCode: "+597" },
    { name: "Sweden", length: 10, countryCode: "+46" },
    { name: "Switzerland", length: 9, countryCode: "+41" },
    { name: "Syria", length: 9, countryCode: "+963" },
    { name: "Taiwan", length: 9, countryCode: "+886" },
    { name: "Tajikistan", length: 9, countryCode: "+992" },
    { name: "Tanzania", length: 10, countryCode: "+255" },
    { name: "Thailand", length: 9, countryCode: "+66" },
    { name: "Timor-Leste", length: 9, countryCode: "+670" },
    { name: "Togo", length: 8, countryCode: "+228" },
    { name: "Tonga", length: 7, countryCode: "+676" },
    { name: "Trinidad and Tobago", length: 10, countryCode: "+1 868" },
    { name: "Tunisia", length: 8, countryCode: "+216" },
    { name: "Turkey", length: 10, countryCode: "+90" },
    { name: "Turkmenistan", length: 8, countryCode: "+993" },
    { name: "Tuvalu", length: 7, countryCode: "+688" },
    { name: "Uganda", length: 10, countryCode: "+256" },
    { name: "Ukraine", length: 9, countryCode: "+380" },
    { name: "United Arab Emirates", length: 9, countryCode: "+971" },
    { name: "United Kingdom", length: 10, countryCode: "+44" },
    { name: "United States", length: 10, countryCode: "+1" },
    { name: "Uruguay", length: 8, countryCode: "+598" },
    { name: "Uzbekistan", length: 9, countryCode: "+998" },
    { name: "Vanuatu", length: 7, countryCode: "+678" },
    { name: "Vatican City", length: 10, countryCode: "+379" },
    { name: "Venezuela", length: 10, countryCode: "+58" },
    { name: "Vietnam", length: 10, countryCode: "+84" },
    { name: "Yemen", length: 9, countryCode: "+967" },
    { name: "Zambia", length: 10, countryCode: "+260" },
    { name: "Zimbabwe", length: 9, countryCode: "+263" },
  ]);
  const [proofFiles, setProofFiles] = useState([]);
  const [phoneError, setPhoneError] = useState("");
  const [pdfUrl, setPdfUrl] = useState("");
  const [isEdit, setIsEdit] = useState(false);
  const [remarks, setRemarks] = useState("");
  const [isSaveChanges, setIsSaveChanges] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [newData, setNewData] = useState([]);
  // Limits served by GET /instalment-plan/get for this account; null until a
  // successful lookup, which means "fall back to the role defaults".
  const [planLimits, setPlanLimits] = useState(null);
  const [instalmentCapWarning, setInstalmentCapWarning] = useState("");

  const roleLimits = ROLE_PLAN_LIMITS[userType] || FALLBACK_PLAN_LIMITS;
  // A null from the API means "no limit" and must win; only an absent field
  // (no lookup yet, or a fresh account) falls back to the role default.
  const maxInstalments =
    planLimits?.maxInstalments !== undefined
      ? planLimits.maxInstalments
      : roleLimits.maxInstalments;
  const canCreateNewPlan = planLimits?.canCreateNewPlan !== false;

  // Limits belong to the account, so drop them -- and any cap warning raised
  // against the old cap -- the moment it changes.
  useEffect(() => {
    setPlanLimits(null);
    setInstalmentCapWarning("");
  }, [accountNo]);

  /**
   * 601 here is the documented "no plans for this account yet" answer, not a
   * failure -- fall back to role defaults and never surface it as an error.
   */
  const loadPlanLimits = async (account) => {
    if (!account) {
      setPlanLimits(null);
      return null;
    }
    try {
      const data = await installmentListApi(account, getCustProjectId());
      if (data?.header?.code === 600 && data?.body?.value) {
        const { maxInstalments: apiMax, minOutstandingAmount, canCreateNewPlan: apiCanCreate } =
          data.body.value;
        const limits = {
          maxInstalments: apiMax,
          minOutstandingAmount,
          canCreateNewPlan: apiCanCreate,
        };
        setPlanLimits(limits);
        return limits;
      }
    } catch (error) {
      console.error("Error loading installment plan limits:", error);
    }
    setPlanLimits(null);
    return null;
  };

  const outstandingAmount = parseFloat(previewData?.outstandingAfterAdvance) || 0;
  const totalEMIAmount = previewData?.instalments ? previewData.instalments.reduce((sum, inst) => sum + (parseFloat(inst.paymentAmount) || 0), 0) : 0;
  const isEMIMismatch = Math.abs(outstandingAmount - totalEMIAmount) > 0.01;

  const validatePhoneNumber = (phone) => {
    let numericPhone = phone.replace(/[^0-9]/g, "");
    const defaultCountry = { name: "Oman", length: 8, countryCode: "968" };

    let country = countryDetail.find((c) =>
      numericPhone.startsWith(c.countryCode.replace(/[^0-9]/g, ""))
    );

    if (!country) {
      country = defaultCountry;
      numericPhone = defaultCountry.countryCode + numericPhone;
    }

    if (country.countryCode === "+1") {
      if (
        countryDetail.name === "United States" &&
        numericPhone.length !== 11
      ) {
        setPhoneError(
          "United States phone numbers should be exactly 10 digits long."
        );
        return false;
      } else if (
        countryDetail.name === "Canada" &&
        numericPhone.length !== 12
      ) {
        setPhoneError("Canada phone numbers should be exactly 11 digits long.");
        return false;
      }
    } else {
      const phoneNumberLength =
        numericPhone.length - country.countryCode.replace(/[^0-9]/g, "").length;

      if (phoneNumberLength !== country.length) {
        setPhoneError(
          `${country.name} phone numbers should be exactly ${country.length} digits long.`
        );
        return false;
      }
    }

    setPhoneError("");
    return true;
  };
  const handlePreviewClick = async () => {
    let isToast;
    // Refresh this account's limits alongside the preview so the cap and the
    // create-blocked state shown next to the schedule are the account's own.
    await loadPlanLimits(accountNo);
    try {
      const response = await PreviewInstallment(
        accountNo, advancePayment, firstInstallmentDate, numInstallments, remarks, getCustProjectId()
      );
      console.log("Preview Response received in try block: ", response);
      if (response?.header?.code === 600) {
        setPreviewData(response.body.value);
        isToast = "sent";
        toast.success(
          typeof response?.body?.value === "string"
            ? response.body.value
            : "Preview generated successfully"
        );
      } else {
        console.log("Error response received. The value to be toasted is: ", response?.body?.value);
        toast.error(
          typeof response?.body?.value === "string"
            ? response.body.value
            : (translations[langMode]?.previewPlanError || "Failed to generate preview")
        );
      }
    } catch (error) {
      console.error("The API call failed entirely, promise rejected:", error);
      if (!isToast) {
        toast.error(translations[langMode]?.somthingwentwrong || "Something went wrong");
      }
    }
    console.log('previewData', previewData)
  };



  const handleSubmit = async () => {
    if (isSubmitting) return;
    try {
      if (!previewData) {
        console.error("No preview data available - generate preview first.");
        toast.error(translations[langMode].pleasegeneratepreview);
        return;
      }
      if (!remarks) {
        toast.error(translations[langMode].RemarkReq)
        return;
      }
      if (!canCreateNewPlan) {
        toast.error(translations[langMode].accountTwoActivePlans);
        return;
      }
      // create-after-edit caps on instalmentDetails.length rather than on the
      // noOfInstalments field, so the submitted row count is what has to
      // satisfy the cap -- keep the two in step before posting.
      const submittedInstalmentCount =
        isEdit && newData.length ? newData.length : Number(numInstallments);
      if (!submittedInstalmentCount || submittedInstalmentCount < 1) {
        toast.error(translations[langMode].instalmentsGreaterThanZero);
        return;
      }
      if (maxInstalments !== null && submittedInstalmentCount > maxInstalments) {
        toast.error(`${translations[langMode].maxInstalmentsExceeded} ${maxInstalments}`);
        return;
      }
      const attachmentError = validateAttachments(proofFiles, langMode);
      if (attachmentError) {
        toast.error(attachmentError);
        return;
      }
      setIsSubmitting(true);
      const blob = await pdf(<MyDocument data={previewData} accountNo={accountNo} />).toBlob();

      if (!blob || blob.size === 0 || blob.type !== "application/pdf") {
        console.error("Generated PDF is invalid", blob);
        toast.error(translations[langMode].failedtogeneratepdf);
        return;
      }
      const pdfFile = new File([blob], "installment_plan.pdf", { type: "application/pdf" });
      const formData = new FormData();
      const commonData = {
        projectId: getCustProjectId(),
        accountNo: accountNo,
        civilId: customerID,
        phoneNo: `968${phone}`,
        noOfInstalments: submittedInstalmentCount,
        advanceAmount: advancePayment,
        advancePaymentDate: advanceDate,
        firstInstalmentDate: firstInstallmentDate,
        remarks: remarks, // 'remarks' included from component state
      };

      if (isEdit) {
        formData.append("instalmentData", JSON.stringify(commonData));
        formData.append("pdfFile", pdfFile); // Append the File
        formData.append("instalmentDetails", JSON.stringify(newData));
        // One `attachments` part per file; the legacy single `attachment`
        // part would only ever carry the first.
        proofFiles.forEach((f) => formData.append("attachments", f));
        console.log("Submitting for Edit with data:", commonData);
        const response = await CreateEditInstallment(formData); // Using the EDIT API

        if (response?.header?.code === 600) {
          toast.success("Installment Plan Updated Successfully!");
          setIsEdit(false);
          setAccountNo("");
          setPhone("");
          setAdvanceDate("");
          setAdvancePayment("");
          setCustomerId("");
          setFirstInstallmentDate("");
          setNumInstallments("");
          setRemarks("");
          setPreviewData(null);
          setNewData([]);
          setProofFiles([])
          setInstalmentCapWarning("")

        } else if (response?.header?.code === 601) {
          toast.error(typeof response?.body?.value === "string" ? response.body.value : "Failed to update installment plan!");
        } else {
          toast.error(response?.body?.value || `Failed to update installment plan! Error Code: ${response?.header?.code || "Unknown"}`);
        }

      } else {

        for (const key in commonData) {
          formData.append(key, commonData[key]);
        }
        formData.append("pdf", pdfFile); // Use "pdf" field name for creation API
        proofFiles.forEach((f) => formData.append("attachments", f));

        console.log("Submitting for Creation with data:", commonData);
        const response = await CreateInstallment(formData); // Using the CREATE API

        if (response?.header?.code === 600) {
          toast.success(translations[langMode].installmentplancretsuc);
          setIsEdit(false);
          setAccountNo("");
          setPhone("");
          setAdvanceDate("");
          setAdvancePayment("");
          setCustomerId("");
          setFirstInstallmentDate("");
          setNumInstallments("");
          setRemarks("");
          setPreviewData(null);
          setProofFiles([])
          setInstalmentCapWarning("")
        } else if (response?.header?.code === 601) {
          toast.error(typeof response?.body?.value === "string" ? response.body.value : "Failed to create installment plan!");
        } else {
          toast.error(response?.body?.value || `Failed to create installment plan! Error Code: ${response?.header?.code || "Unknown"}`);
        }
      }
    } catch (error) {
      console.error("handleSubmit error:", error);
      toast.error("Error generating PDF or submitting installment plan!");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePrint = () => {
    const printContents = tableRef.current.innerHTML;
    const printWindow = window.open('', '', 'height=600,width=800');
    printWindow.document.write('<html><head><title>Print</title>');
    printWindow.document.write('<style>body{font-family:MediumFont, Manrope, sans-serif;padding:20px;} table{width:100%;border-collapse:collapse;} th,td{border:1px solid #ccc;padding:8px;text-align:center;}</style>');
    printWindow.document.write('</head><body>');
    printWindow.document.write(printContents);
    printWindow.document.write('</body></html>');
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  // Same "child pushes a control into the parent-owned header" hand-off
  // used by ViewInstallment.jsx's search/print controls -- logic/state
  // (handlePrint, previewData) stays here, only where the button renders
  // moves up to CustomerSupport.jsx's header row.
  useEffect(() => {
    if (!onHeaderActionsChange) return undefined;

    onHeaderActionsChange(
      <div className="flex w-full flex-wrap items-center justify-end gap-2 sm:w-auto">
        <CommonButton colorIntent="primary"
          onClick={handlePrint}
          variant="primary"
          tooltipText={translations[langMode].printpreviewtable}
          tooltipPosition="top"
          disabled={!previewData}
          className="shrink-0 justify-center !h-10 !min-h-[40px] !rounded-[10px] px-3 py-2 text-xs sm:px-4 sm:text-sm"
        >
          {translations[langMode].Print}
        </CommonButton>
        <CommonButton colorIntent="warning"
          onClick={() => setPreviewData(null)}
          variant="danger"
          className="shrink-0 whitespace-nowrap !rounded-lg !px-4 !py-2.5 !gap-2"
          tooltipText={translations[langMode].clearpreviewdata}
          tooltipPosition="top"
        >
          {translations[langMode].clrPreview}
        </CommonButton>
        <CommonButton colorIntent="primary"
          onClick={handlePreviewClick}
          variant="primary"
          className="shrink-0 whitespace-nowrap !rounded-lg !px-4 !py-2.5 !gap-2"
          tooltipText={translations[langMode].generatepreview}
          tooltipPosition="top"
        >
          {translations[langMode].prevPlan}
        </CommonButton>
        <CommonButton colorIntent="success"
          onClick={handleSubmit}
          variant="primary"
          className={`create-plan shrink-0 whitespace-nowrap !rounded-lg !px-4 !py-2.5 !gap-2 ${(!previewData ||
            isEMIMismatch ||
            isSaveChanges ||
            isSubmitting ||
            totalEMIAmount < 0 ||
            !canCreateNewPlan || !customVarPerm)
            ? "cursor-not-allowed !bg-blue-300 hover:bg-blue-300"
            : ""
            }`}
          disabled={
            !previewData ||
            isEMIMismatch ||
            isSaveChanges ||
            isSubmitting ||
            totalEMIAmount < 0 ||
            !canCreateNewPlan || !customVarPerm
          }
          tooltipText={translations[langMode].createinstallmentplan + ((!previewData && translations[langMode].generatepreviewfiter) || (isEMIMismatch && translations[langMode].emiamount) || (isSaveChanges && translations[langMode].savechangesbeforesubmiting) || (totalEMIAmount < 0 && translations[langMode].totalemaiamount) || (!canCreateNewPlan && translations[langMode].maxTwoActivePlans))}
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>{translations[langMode].createInstallment}</span>
            </span>
          ) : (
            translations[langMode].createInstallment
          )}
        </CommonButton>
      </div>
    );

    return () => onHeaderActionsChange(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    accountNo,
    advancePayment,
    firstInstallmentDate,
    remarks,
    customerID,
    advanceDate,
    numInstallments,
    proofFiles,
    previewData,
    isEdit,
    newData,
    isEMIMismatch,
    isSaveChanges,
    isSubmitting,
    totalEMIAmount,
    canCreateNewPlan,
    maxInstalments,
    customVarPerm,
    langMode,
  ]);

  const formatPhoneNumber = (value) => {
    console.log("value", value)
    const cleaned = value.replace(/\D/g, "").slice(0, 8);
    if (cleaned.length === 0) return ""; // <-- fix to allow clearing

    const match = cleaned.match(/^(\d{0,4})(\d{0,4})$/);
    if (!match) return "";

    const [, part1, part2] = match;
    return `${part1}${part2 ? " " + part2 : ""}`;
  };

  return (
    <div className="w-full min-w-0 max-w-full">
      <div className="mb-3">
        <label className="text-sm font-semibold">{translations[langMode].custDetail}</label>
      </div>
      <div className="mb-5 grid min-w-0 grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        <input
          type="number"
          value={accountNo}
          onChange={(e) => setAccountNo(e.target.value)}
          onBlur={(e) => loadPlanLimits(e.target.value)}
          placeholder={translations[langMode].accno}
          className={`${FIELD_INPUT_CLASS} focus:shadow-none focus-visible:outline-none focus-visible:ring-0 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none`}
        />
        <input
          type="text"
          value={customerID}
          onChange={(e) => setCustomerId(e.target.value)}
          placeholder={translations[langMode].CustomerId}
          className={FIELD_INPUT_CLASS}
        />

        <div>
          <div className="flex h-[50px] items-center rounded-lg w-full bg-white px-3 border border-[#D1D5DB] text-sm focus-within:border-blue-500">
            <div className="flex items-center gap-2">
              <span>OM</span>
              <span className="font-bold">+968</span>
              <ChevronDown size={14} className="text-gray-400" />
            </div>
            <input
              className={`block px-2 py-1 w-full text-sm text-gray-900 bg-transparent rounded-md
                            appearance-none border-0 focus:ring-0 focus:!shadow-none peer ${theme === "dark"
                  ? "dark-mode"
                  : theme === "high-contrast"
                    ? "high-contrast"
                    : ""
                }`}
              type="tel"
              name="phone"
              value={phone}
              onChange={(e) => {
                const raw = e.target.value;
                const cleaned = raw.replace(/\D/g, "");
                const formatted = formatPhoneNumber(cleaned);
                setPhone(formatted)
              }}
              placeholder="9123 4567"
              maxLength={9}
            />
          </div>
          {phoneError && (
            <div className="text-xs" style={{ color: "red", marginTop: "2px" }}>
              {phoneError}
            </div>
          )}
        </div>

        <div className="relative w-full">
          <input
            type="text"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder="Remarks"
            className={FIELD_INPUT_CLASS}
          />
          <span className="absolute right-2 top-1 text-red-500">*</span>
        </div>

      </div>
      <div className="mb-3">
        <label className="text-sm font-semibold">{translations[langMode].instalDetail}</label>
      </div>
      <div className="mb-5 grid min-w-0 grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        <div className="flex flex-col gap-1">
          <input
            type="number"
            value={numInstallments}
            onChange={(e) => {
              const val = e.target.value;
              if (val === "") {
                setInstalmentCapWarning("");
                setNumInstallments(val);
                return;
              }
              if (!/^\d+$/.test(val)) return;
              // Over the cap: hold the last valid value but say why, rather
              // than silently swallowing the keystroke.
              if (maxInstalments !== null && Number(val) > maxInstalments) {
                setInstalmentCapWarning(
                  `${translations[langMode].maxInstalmentsExceeded} ${maxInstalments}`
                );
                return;
              }
              setInstalmentCapWarning("");
              setNumInstallments(val);
            }}
            placeholder={translations[langMode].NoOfinstallment}
            className={`${FIELD_INPUT_CLASS} focus:shadow-none focus-visible:outline-none focus-visible:ring-0 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none`}

          />

          {instalmentCapWarning && (
            <span className="text-red-500 text-sm">{instalmentCapWarning}</span>
          )}
          {!canCreateNewPlan && (
            <span className="text-red-500 text-sm">
              {translations[langMode].accountTwoActivePlans}
            </span>
          )}
        </div>

        <div className="flex flex-col">
          <input
            type="number"
            value={advancePayment}
            onChange={(e) => setAdvancePayment(e.target.value)}
            placeholder={translations[langMode].advancepayamt}
            className={`${FIELD_INPUT_CLASS} focus:shadow-none focus-visible:outline-none focus-visible:ring-0 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none`}
          />
        </div>

        <div className="flex flex-col h-auto xl:col-start-1">
          <label className="text-sm font-semibold mb-1 ml-2">
            {translations[langMode].advDate}
          </label>
          <DatePicker
            selected={parseDateTime(advanceDate)}
            onChange={(date) => setAdvanceDate(date ? formatDateTimeForApi(date) : "")}
            showTimeSelect
            timeFormat="HH:mm"
            timeIntervals={15}
            dateFormat="yyyy-MM-dd HH:mm"
            placeholderText="YYYY-MM-DD HH:mm"
            wrapperClassName="w-full block"
            className={installmentDatePickerClassName(theme)}
            calendarClassName="!rounded-lg shadow-lg"
            popperPlacement="bottom-start"
            popperContainer={installmentDatePickerPopper}
            popperProps={{ strategy: "fixed" }}
            popperModifiers={installmentDatePickerModifiers}
            showPopperArrow={false}
          />
        </div>

        <div className="flex flex-col">
          <label className="text-sm font-semibold mb-1 ml-2">
            {translations[langMode].firstInstDate}
          </label>
          <DatePicker
            selected={parseDateTime(firstInstallmentDate)}
            onChange={(date) => setFirstInstallmentDate(date ? formatDateTimeForApi(date) : "")}
            showTimeSelect
            timeFormat="HH:mm"
            timeIntervals={15}
            dateFormat="yyyy-MM-dd HH:mm"
            placeholderText="YYYY-MM-DD HH:mm"
            wrapperClassName="w-full block"
            className={installmentDatePickerClassName(theme)}
            calendarClassName="!rounded-lg shadow-lg"
            popperPlacement="bottom-start"
            popperContainer={installmentDatePickerPopper}
            popperProps={{ strategy: "fixed" }}
            popperModifiers={installmentDatePickerModifiers}
            showPopperArrow={false}
          />
        </div>

        <MultiAttachInput
          files={proofFiles}
          onChange={setProofFiles}
          theme={theme}
          langMode={langMode}
          showNote={false}
        />
      </div>

      {previewData &&
        <div ref={tableRef}>
          <PaymentTable pevData={previewData} setIsEdit={setIsEdit} isEdit={isEdit} setNewData={setNewData} newData={newData} isSaveChanges={isSaveChanges} setIsSaveChanges={setIsSaveChanges} />
        </div>}

      {isPreviewVisible && pdfData && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-50">
          <PDFViewerComponent
            data={previewData}
            onClose={() => setIsPreviewVisible(false)}
            setPdfUrl={setPdfUrl}
          />
        </div>
      )}
    </div>
  );
};

export default CreateInstallmentPlan;

